import React, { useCallback, useEffect, useMemo } from 'react';
import {
    Route, Routes, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { useClientLayout } from 'pages/ClientOverview/hooks/useClientLayout';
import RouteBasedStepperLayout from 'prodivers/RouteBasedStepperLayout';
import { flattenSteps } from 'utils/stepDefintion';
import { useTranslation } from 'react-i18next';
import { useClientProfile } from 'prodivers/clientProfile';
import { getClientId } from 'utils';
import { CEP_PRODUCT_KEY } from 'constants/constants';
import { useChangeExistingPlanLayout } from './hooks/useChangeExistingPlanLayout';
import useChangeExistingPlanFlow from './hooks/useChangeExistingPlanFlow';
import { StepControllerProvider } from '../../../../../../components/Stepper/RouteBasedStepper/StepControllerContext';
import { ChangeStrategyProvider } from './context';
import './changeExistingPlan.css';

const Router = () => {
    const { portfolioId, dfsClientId } = useParams();
    const { state } = useLocation();
    const clientId = getClientId(dfsClientId);
    const productKey = useMemo(() => sessionStorage.getItem(CEP_PRODUCT_KEY) ?? state?.productKey, [state?.productKey]);

    useEffect(() => {
        if (state?.productKey) {
            sessionStorage.setItem(CEP_PRODUCT_KEY, state?.productKey);
        }
    }, [state?.productKey]);

    const navigate = useNavigate();
    const { t, i18n: { language } } = useTranslation();
    const {
        clientName,
    } = useClientProfile();
    const { stepsBasedOnState } = useChangeExistingPlanFlow();
    const steps = useMemo(() => flattenSteps(stepsBasedOnState), [stepsBasedOnState]);

    const headerNavLeftButtonOnLeftButtonClick = useCallback(
        () => { navigate(`/client/${dfsClientId}/`); },
        [dfsClientId],
    );

    const headerNavLeftButton = useMemo(
        () => ({ type: 'login', label: `${t('clientDashboard.backTo')} ${clientName}` }),
        [clientName, language],
    );

    useClientLayout(
        false,
        false,
        headerNavLeftButton,
        headerNavLeftButtonOnLeftButtonClick,
    );

    return (
        <ChangeStrategyProvider clientId={clientId as number} portfolioId={portfolioId as string} productKey={productKey}>
            <StepControllerProvider>
                <RouteBasedStepperLayout dataSource={useChangeExistingPlanLayout}>
                    <Routes>
                        {Object.keys(steps).map((key) => {
                            const route = steps[key];
                            const Component = route.component;

                            return (
                                <Route
                                    path={route.url}
                                    key={key}
                                    element={<Component {...{ datasource: route.datasource }}/>}
                                />
                            );
                        })}
                    </Routes>
                </RouteBasedStepperLayout>
            </StepControllerProvider>
        </ChangeStrategyProvider>
    );
};

Router.displayName = 'Router';

export default Router;
